/**
 * Copy for the Files surface's body — the one destination list every role sees
 * (SPEC §6, FIL-1d, FIL-1e).
 *
 * **Several strings here read almost identically to `SPACE_LIST_COPY`'s, and
 * that is deliberate rather than drift.** The two components sit on different
 * surfaces — this one on Files, that one on Spaces — and a reader crossing
 * between them must not be told two different things about the same state.
 * Duplicate the wording, say in a comment that it is duplicated, and do not
 * import one component's copy into another.
 */
export const FOLDER_DESTINATIONS_COPY = {
  loading: 'Loading folders…',
  error: 'Folders could not be loaded.',
  forbidden: 'You do not have access to these folders.',
  retry: 'Try again',

  /** The list's three columns, and `DataTable`'s required accessible caption. */
  columns: {
    folder: 'Folder',
    details: 'Details',
    lastActivity: 'Last activity',
  },
  tableCaption: 'Folders',

  /** Detail copy shared by destination cards and rows. */
  details: {
    empty: 'Empty',
    countAndSize: (count: number, size: string) =>
      `${count === 1 ? '1 file' : `${String(count)} files`}, ${size}`,
    lastActivity: (date: string) => `Last activity on ${date}`,
  },

  states: {
    /**
     * PERM-2: a member with no grants sees an empty Files surface.
     *
     * **There is no longer a "spaces exist but none is open" third state.**
     * Every space a caller can reach contributes a root destination (FIL-1e),
     * so an empty list means no `Active` membership at all — which is the one
     * sentence this used to need three of.
     */
    empty: 'No folders yet. An admin at your agency grants access to a shared space.',

    /**
     * The same nothing, said to the person who can fix it.
     *
     * **This string is what keeps the surface from being a dead end.** Creating
     * a space is on the Spaces surface, so a brand-new agency's first admin
     * opens Files, finds nothing, and has no control in front of them at all.
     * The sentence and the link below it are the path back.
     */
    emptyAdmin: 'No shared spaces yet. Create one to start sharing.',

    /** To the Spaces surface, beside the admin state above. */
    manage: 'Go to Spaces',
  },

  /**
   * Bytes, at the scale evidence actually arrives in — `SPACE_COPY.units`'
   * same ladder, duplicated on this file's own precedent above.
   */
  units: ['B', 'KB', 'MB', 'GB', 'TB'],
} as const;
